import { useCallback, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import {
  PermitRequestForm,
  buildSubactivityFieldKey,
  type WorkerSelectionItem,
  type CreatePermitRequestPayload,
  type CreatePermitResponsePayload,
} from "modules/permit"
import { type RiskSnapshot, type SummaryActionNotice } from "modules/permit/components/PermitRequestSummaryPanel"
import { PageTitle } from "shared/components/layout/PageTitle"
import type { WorkAreaSelection } from "modules/permit/components/WorkAreaSection"
import type { UploadedFileItem } from "modules/permit/components/RequiredDocumentsSection"
import { BottomActionBar } from "modules/permit/components/BottomActionBar"
import { GENERAL_FIELD_STATE_MAP, GENERAL_SECTION_CODE } from "modules/permit/utils/permitFieldMapping"
import { buildTemplateFieldLabelKey } from "modules/permit/components/permit-request/utils/templateFieldUtils"
import {
  createEmptyRiskState,
  buildRiskAssessmentPayload,
  normaliseAreaList,
  normaliseGpsAreaList,
  normaliseMarkerValue,
  parseNullableNumber,
  toStringOrUndefined,
  type RiskState,
} from "modules/permit/utils/permitHelpers"
import { useActiveWorkflowStage, useWorkflowMetadata } from "modules/permit/hooks/useWorkflowStage"
import { usePermitMasterData } from "./permit-request/hooks/usePermitMasterData"
import { usePermitDetailHydration } from "./permit-request/hooks/usePermitDetailHydration"
import { usePermitWorkflowTimeline } from "./permit-request/hooks/usePermitWorkflowTimeline"
import { usePermitDocuments } from "./permit-request/hooks/usePermitDocuments"
import { usePermitRequestActions } from "./permit-request/hooks/usePermitRequestActions"
import { useWorkflowApprovalActions } from "./permit-request/hooks/useWorkflowApprovalActions"
// import { RejectionAlert } from "./permit-request/components/RejectionAlert"
// import type { WorkflowRejectionNote } from "./permit-request/components/RejectionAlert"
import { buildPermitPayload as buildPermitRequestPayload } from "./permit-request/utils/payloadBuilder"
import type { PermitFormState, PermitRequestLayoutState } from "./permit-request/types"
import {
  isCancelledStatus,
  isDraftStatus,
  isEditablePermitStatus,
  isOpenStatus,
  isRequesterInformationStatus,
  isReadyToOpenStatus,
  isRevisionStatus,
  normalizePermitStatus,
} from "modules/permit/constants/permitStatus"
import { usePermitValidation } from "./permit-request/hooks/usePermitValidation"
import { PermitRequestActionProvider } from "modules/permit/context/PermitRequestActionsContext"
import { PermitRequestSidebar } from "./permit-request/components/PermitRequestSidebar"
import { WorkflowDialogsContainer } from "./permit-request/components/WorkflowDialogsContainer"
import { Button } from "shared/ui/button"
import { Card, CardContent } from "shared/ui/card"
import { type PermitDetail } from "modules/permit/api/permitService"
import { CancelPermitDialog } from "./permit-request/components/CancelPermitDialog"
import { ChangeWorkTimeDialog } from "./permit-request/components/ChangeWorkTimeDialog"
import { ChangeWorkTimeResultDialog } from "./permit-request/components/ChangeWorkTimeResultDialog"
import { ExtendPermitDialog } from "./permit-request/components/ExtendPermitDialog"
import { ExtendPermitResultDialog } from "./permit-request/components/ExtendPermitResultDialog"
import { ReferencePermitDialog } from "./permit-request/components/ReferencePermitDialog"
import { usePermitCancelFlow } from "./permit-request/hooks/usePermitCancelFlow"
import { usePermitReferenceFlow } from "./permit-request/hooks/usePermitReferenceFlow"
import { useChangeWorkTimeFlow } from "./permit-request/hooks/useChangeWorkTimeFlow"
import { useExtendPermitFlow } from "./permit-request/hooks/useExtendPermitFlow"
import { usePermitSummaryActionNotice } from "./permit-request/hooks/usePermitSummaryActionNotice"
import {
  ALWAYS_EDITABLE_SECTION_ROLE_CODES,
  ALWAYS_EDITABLE_STATUS_CODES,
  DEFAULT_PERMIT_DATA,
  DRAFT_STATUS,
  TENANT_ID,
  THAI_CHANGE_WORK_TIME_LABEL,
  THAI_EXTEND_PERMIT_LABEL,
  THAI_SUBMIT_APPROVAL_LABEL,
  collectNormalizedUserRoleCodeCandidates,
  formatTimeFromIso,
  getPermitPageCopy,
  parseTimeValue,
  resolveEffectiveEntryMode,
  resolveNormalizedUserId,
  resolveUserCompanyId,
  resolveUserCompanyName,
  resolveUserDisplayName,
} from "./permit-request/utils/pageHelpers"
import { getRequesterInfoActionVisibility } from "./permit-request/utils/requesterInfoActionVisibility"
import { resolveWorkerCompanyName, resolveWorkerJobRole } from "modules/permit/utils/workerSelection"
import { resolveTenantId } from "shared/utils/tenantContext"
import { Loader2 } from "lucide-react"

/**
 * Top-level page container responsible for orchestrating permit request workflow:
 * - handles navigation guards and layout state wiring
 * - loads template configuration / permit detail data
 * - coordinates saving drafts via the feature-level API helpers
 */
export function PermitRequestPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const state = useMemo(() => {
    const locationState = (location.state as PermitRequestLayoutState | undefined) ?? {}
    if (locationState.permitId) {
      return locationState
    }
    // A permit opened via window.open() (e.g. "เปิดดู" in a new tab) has no
    // router state to read - fall back to the URL query string instead.
    const urlPermitId = searchParams.get("permitId")
    if (!urlPermitId) {
      return locationState
    }
    const urlTenantId = searchParams.get("tenantId")
    return {
      ...locationState,
      permitId: urlPermitId,
      mode: (searchParams.get("mode") as PermitRequestLayoutState["mode"]) ?? locationState.mode,
      entryMode:
        (searchParams.get("entryMode") as PermitRequestLayoutState["entryMode"]) ?? locationState.entryMode,
      forceReadOnly: searchParams.get("forceReadOnly") === "true" || locationState.forceReadOnly,
      hideActionBar: searchParams.get("hideActionBar") === "true" || locationState.hideActionBar,
      site: urlTenantId ? { ...locationState.site, id: urlTenantId } : locationState.site,
    } satisfies PermitRequestLayoutState
  }, [location.state, searchParams])
  const entryMode = state.entryMode
  // Only honor read-only flags when caller explicitly wants a view-only flow; never force read-only in edit mode.
  const forceReadOnly = state.forceReadOnly === true && entryMode !== "edit"
  const hideActionBar = state.hideActionBar === true && entryMode !== "edit"
  const resolvedUserName = useMemo(() => resolveUserDisplayName(state.user), [state.user])
  const resolvedCompanyName = useMemo(() => resolveUserCompanyName(state.user), [state.user])
  const resolvedCompanyId = useMemo(() => resolveUserCompanyId(state.user), [state.user])
  const currentUser = useMemo(
    () => ({
      ...(state.user ?? {}),
      name: resolvedUserName,
      company: resolvedCompanyName,
      companyId: resolvedCompanyId,
      // Some sessions carry the role list at state.roles (top-level) rather than
      // state.user.roles - mirrors the same fallback workflowUserContext already applies, so
      // role-code resolution (e.g. ALWAYS_EDITABLE_SECTION_ROLE_CODES) is consistent everywhere.
      roles:
        (Array.isArray((state.user as any)?.roles) && (state.user as any).roles) ||
        (Array.isArray((state as any)?.roles) && (state as any).roles) ||
        undefined,
    }),
    [state, resolvedUserName, resolvedCompanyName, resolvedCompanyId],
  )
  const currentUserType = useMemo(() => {
    const candidate = state.user?.userType ?? state.user?.user_type ?? state.user?.type
    return typeof candidate === "string" ? candidate.trim().toUpperCase() : ""
  }, [state.user])
  const isContractorUser = currentUserType === "CONTRACTOR"
  const normalizeCompanyToken = useCallback((value?: string | null) => {
    if (typeof value !== "string") {
      return ""
    }
    return value.trim().replace(/\s+/g, " ").toLowerCase()
  }, [])

  useEffect(() => {
    console.debug("[permit][request-page] currentUser resolved", {
      rawUser: state.user,
      resolvedUserName,
      resolvedCompanyName,
      resolvedCompanyId,
      companyD: (state.user as any)?.companyD,
    })
  }, [state.user, resolvedUserName, resolvedCompanyName, resolvedCompanyId])
  const initialMode: "create" | "edit" = state.mode ?? (state.permitId ? "edit" : "create")
  const [mode, setMode] = useState<"create" | "edit">(initialMode)
  const effectiveEntryMode = resolveEffectiveEntryMode({ entryMode, mode, forceReadOnly })
  const pageCopy = useMemo(() => getPermitPageCopy(effectiveEntryMode), [effectiveEntryMode])
  const [editingPermitId, setEditingPermitId] = useState<string | null>(state.permitId ?? null)
  const normalizedCurrentUserId = useMemo(() => resolveNormalizedUserId(state.user), [state.user])
  const resolvedTenantId = useMemo(() => {
    return resolveTenantId(
      state.site?.id,
      (state.user as any)?.tenantId,
      (state.user as any)?.tenant_id,
      TENANT_ID,
    )
  }, [state.site?.id, state.user])

  const [permitData, setPermitData] = useState({
    ...DEFAULT_PERMIT_DATA,
    requestorName: resolvedUserName,
    requestorCompany: resolvedCompanyName,
  })
  useEffect(() => {
    setPermitData((prev) => ({
      ...prev,
      requestorName: prev.requestorName || resolvedUserName,
      requestorCompany: prev.requestorCompany || resolvedCompanyName,
    }))
  }, [resolvedUserName, resolvedCompanyName])
  const [generalFieldLabels, setGeneralFieldLabels] = useState<Record<string, string>>({})
  const [selectedWorkers, setSelectedWorkers] = useState<WorkerSelectionItem[]>([])
  const [selectedActivities, setSelectedActivities] = useState<string[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileItem[]>([])
  const [extraFieldValues, setExtraFieldValues] = useState<Record<string, string>>({})
  const [riskData, setRiskData] = useState<RiskState>(createEmptyRiskState())
  const [workAreaSelection, setWorkAreaSelection] = useState<WorkAreaSelection>({
    mapMode: "plot-plan",
    marker: null,
    area: null,
    areas: null,
    gpsLatitude: null,
    gpsLongtitude: null,
    gpsAreas: null,
  })
  const [approvalStateId, setApprovalStateId] = useState<string | null>(null)
  const [pendingPostActionCheck, setPendingPostActionCheck] = useState<"submit" | "approve" | null>(null)
  const [hasObservedPostActionReload, setHasObservedPostActionReload] = useState(false)
  const {
    permitTypes,
    isLoadingPermitTypes,
    permitTypesError,
    templateConfig,
    isTemplateLoading,
    templateError,
  } = usePermitMasterData({
    tenantId: resolvedTenantId,
    permitTypeCode: permitData.permitTypeCode,
    approvalStateId,
  })
  const riskSnapshot = useMemo<RiskSnapshot>(() => {
    const payload = buildRiskAssessmentPayload(riskData)
    if (!payload) {
      return {
        level: "Not Assessed",
        riskScore: 0,
        consequenceScore: 0,
        likelihoodScore: 0,
      }
    }
    return {
      level: payload.riskLevel,
      riskScore: payload.riskScore,
      consequenceScore: payload.consequenceScore,
      likelihoodScore: payload.likelihoodScore,
    }
  }, [riskData])

  const applyReferenceFromPermitDetail = useCallback(
    (permitDetail: PermitDetail) => {
      if (!permitDetail) return

      const metadata = permitDetail.metadata ?? {}
      const generalUpdates: Partial<PermitFormState> = {
        workDescription: "",
        workLocation: "",
        unit: "",
        building: "",
        floor: "",
        room: "",
        other: "",
        startDate: "",
        endDate: "",
        startTime: "",
        endTime: "",
        hazardousArea: "",
      }
      const generalLabelUpdates: Record<string, string> = {}
      const extraValues: Record<string, string> = {}
      let controllerSelection: PermitFormState["controller"] = null
      const generalSectionPrefix = GENERAL_SECTION_CODE.toUpperCase()

      const resolveFieldValue = (field: PermitDetail["fieldValues"][number]): string => {
        if (field.valueText !== undefined && field.valueText !== null) return String(field.valueText)
        if (field.valueBoolean !== undefined && field.valueBoolean !== null) return String(field.valueBoolean)
        if (field.valueNumber !== undefined && field.valueNumber !== null) return String(field.valueNumber)
        if (field.valueDate !== undefined && field.valueDate !== null) return String(field.valueDate)
        if (field.valueTimestamp !== undefined && field.valueTimestamp !== null) return String(field.valueTimestamp)
        return ""
      }

      ;(permitDetail.fieldValues ?? []).forEach((field) => {
        const code = field.fieldCode?.trim()
        if (!code) return
        const upper = code.toUpperCase()
        const sectionCode = field.sectionCode?.trim().toUpperCase() ?? ""
        const isGeneralSection = sectionCode.startsWith(generalSectionPrefix)
        if (!isGeneralSection) return
        const stateKey = GENERAL_FIELD_STATE_MAP[upper]
        const fieldType = (field.fieldDataType ?? "").toLowerCase()
        const resolvedValue = resolveFieldValue(field)

        if (stateKey) {
          if (upper === "STDATE" || upper === "ENDATE") {
            return
          }
          const displayValue = typeof field.displayValue === "string" ? field.displayValue.trim() : ""
          if (displayValue) {
            generalLabelUpdates[upper] = displayValue
          }
          if (upper === "CONTROLLER") {
            const controllerLabel =
              permitDetail.metadata?.["controllerLabel"] ?? permitDetail.metadata?.["controller"] ?? ""
            controllerSelection = {
              value: resolvedValue,
              label: controllerLabel || resolvedValue,
            }
            return
          }
          switch (fieldType) {
            case "date":
              generalUpdates[stateKey as keyof PermitFormState] = (field.valueDate ?? "") as any
              break
            case "time": {
              const time = parseTimeValue(field.valueText)
              if (upper === "STTIME") {
                generalUpdates.startTime = time
              } else if (upper === "ENTIME") {
                generalUpdates.endTime = time
              } else {
                generalUpdates[stateKey as keyof PermitFormState] = (field.valueText ?? "") as any
              }
              break
            }
            case "checkbox":
            case "boolean":
              generalUpdates[stateKey as keyof PermitFormState] = (field.valueBoolean ?? false) as any
              break
            case "number":
            case "numeric":
              generalUpdates[stateKey as keyof PermitFormState] =
                field.valueNumber !== undefined && field.valueNumber !== null
                  ? (String(field.valueNumber) as any)
                  : ((field.valueText ?? "") as any)
              break
            default:
              generalUpdates[stateKey as keyof PermitFormState] =
                (field.valueText ??
                  (field.valueBoolean !== undefined && field.valueBoolean !== null
                    ? field.valueBoolean
                    : field.valueNumber ?? field.valueDate ?? "")) as any
          }
        }

        const fallback = resolvedValue
        const shouldStoreInExtraValues = !stateKey && upper !== "CONTROLLER"
        if (shouldStoreInExtraValues && fallback && fallback.trim().length > 0) {
          extraValues[upper] = fallback
          if (field.fieldId) {
            extraValues[field.fieldId] = fallback
          }
          const fieldType = (field.fieldDataType ?? "").toLowerCase()
          const displayValue = typeof field.displayValue === "string" ? field.displayValue.trim() : ""
          if (
            displayValue &&
            (fieldType === "search_select" || fieldType === "select" || fieldType === "multiselect")
          ) {
            extraValues[buildTemplateFieldLabelKey(upper)] = displayValue
            if (field.fieldId) {
              extraValues[buildTemplateFieldLabelKey(field.fieldId)] = displayValue
            }
          }
        }
      })

      const selectedSubactivityIds: string[] = []
      const subactivityFieldValues: Record<string, string> = {}

      permitDetail.selectedSubActivities?.forEach((subactivity) => {
        selectedSubactivityIds.push(subactivity.subactivityId)
        subactivity.fieldValues?.forEach((field) => {
          if (!field.fieldId) return
          const key = buildSubactivityFieldKey(subactivity.subactivityId, field.fieldId)
          if (field.valueText !== undefined && field.valueText !== null) {
            subactivityFieldValues[key] = field.valueText
          }
        })
      })

      if (!generalUpdates.startTime) {
        const time = formatTimeFromIso(permitDetail.plannedStart)
        if (!generalUpdates.startTime) generalUpdates.startTime = time
      }
      if (!generalUpdates.endTime) {
        const time = formatTimeFromIso(permitDetail.plannedEnd)
        if (!generalUpdates.endTime) generalUpdates.endTime = time
      }

      const selectedPermitType =
        permitTypes.find((type) => type.permitCode === permitDetail.permitTypeCode)?.permitType ??
        permitDetail.permitTypeName ??
        permitData.permitType

      setPermitData((prev) => ({
        ...prev,
        permitNo: "",
        permitType: selectedPermitType,
        permitTypeCode: permitDetail.permitTypeCode || prev.permitTypeCode,
        requestorName:
          permitDetail.requesterName ??
          metadata["requestorName"] ??
          prev.requestorName ??
          currentUser.name ??
          prev.requestorName,
        requestorCompany:
          permitDetail.requesterCompany ??
          metadata["requestorCompany"] ??
          prev.requestorCompany ??
          currentUser.company ??
          prev.requestorCompany,
        controller: controllerSelection,
        ...generalUpdates,
      }))

      setSelectedActivities(selectedSubactivityIds)

      const workerSelections = (permitDetail.workers ?? []).map((worker) => ({
        workerId: worker.workerId,
        companyId: worker.companyId,
        companyName: resolveWorkerCompanyName(worker) ?? "",
        orgName: worker.orgName ?? null,
        fullName: worker.fullName,
        username: worker.username ?? null,
        jobRole: resolveWorkerJobRole(worker),
        status: worker.status,
        cardNumber: worker.cardNumber ?? null,
        imageType: worker.imageType ?? null,
        imagePath: worker.imagePath ?? null,
        roleInPermit: worker.roleInPermit ?? null,
      }))

      setSelectedWorkers(workerSelections)
      if (Object.keys(generalLabelUpdates).length > 0) {
        setGeneralFieldLabels((prev) => ({
          ...prev,
          ...generalLabelUpdates,
        }))
      }

      const combinedExtraValues: Record<string, string> = {
        ...extraValues,
        ...subactivityFieldValues,
      }
      setExtraFieldValues(combinedExtraValues)

      const detailWorkArea = permitDetail.workArea
      if (detailWorkArea) {
        const mapMode = detailWorkArea.mapMode === "gps" ? "gps" : "plot-plan"
        const gpsAreas =
          detailWorkArea.gpsAreas && detailWorkArea.gpsAreas.length > 0
            ? detailWorkArea.gpsAreas.map((item) => ({
                north: item.north,
                south: item.south,
                east: item.east,
                west: item.west,
              }))
            : normaliseGpsAreaList(metadata["workAreaGpsAreas"])
        const areas =
          detailWorkArea.areas && detailWorkArea.areas.length > 0
            ? detailWorkArea.areas.map((item) => ({
                xPercent: item.xPercent,
                yPercent: item.yPercent,
                widthPercent: item.widthPercent,
                heightPercent: item.heightPercent,
              }))
            : detailWorkArea.area
              ? [
                  {
                    xPercent: detailWorkArea.area.xPercent,
                    yPercent: detailWorkArea.area.yPercent,
                    widthPercent: detailWorkArea.area.widthPercent,
                    heightPercent: detailWorkArea.area.heightPercent,
                  },
                ]
              : []
        const lastArea = areas.length > 0 ? areas[areas.length - 1] : null

        setWorkAreaSelection({
          mapMode,
          plotPlanId: toStringOrUndefined(detailWorkArea.plotPlanId),
          plotPlanVersionId: toStringOrUndefined(detailWorkArea.plotPlanVersionId),
          plotPlanName: toStringOrUndefined(detailWorkArea.plotPlanName),
          plotPlanImagePath: toStringOrUndefined(detailWorkArea.plotPlanImagePath),
          marker:
            mapMode === "gps" || !detailWorkArea.marker
              ? null
              : {
                  xPercent: detailWorkArea.marker.xPercent,
                  yPercent: detailWorkArea.marker.yPercent,
                },
          area: lastArea,
          areas: areas.length > 0 ? areas : null,
          gpsLatitude: detailWorkArea.gpsLatitude ?? null,
          gpsLongtitude: detailWorkArea.gpsLongtitude ?? null,
          gpsAreas: gpsAreas.length > 0 ? gpsAreas : null,
        })
      } else {
        const marker = normaliseMarkerValue(metadata["workAreaMarker"])
        const areasFromList = normaliseAreaList(metadata["workAreaAreas"])
        const areasFromSingle = normaliseAreaList(metadata["workAreaArea"])
        const areas = areasFromList.length > 0 ? areasFromList : areasFromSingle
        const area = areas.length > 0 ? areas[areas.length - 1] : null

        const workAreaMapModeRaw =
          typeof metadata["workAreaMapMode"] === "string"
            ? metadata["workAreaMapMode"].toLowerCase()
            : ""

        setWorkAreaSelection({
          mapMode: workAreaMapModeRaw === "gps" ? "gps" : "plot-plan",
          plotPlanId: toStringOrUndefined(metadata["workAreaPlotPlanId"]),
          plotPlanVersionId: toStringOrUndefined(metadata["workAreaPlotPlanVersionId"]),
          plotPlanName: toStringOrUndefined(metadata["workAreaPlotPlanName"]),
          plotPlanImagePath: toStringOrUndefined(metadata["workAreaPlotPlanImagePath"]),
          marker,
          area,
          areas: areas.length > 0 ? areas : null,
          gpsLatitude: parseNullableNumber(metadata["workAreaGpsLatitude"]),
          gpsLongtitude: parseNullableNumber(metadata["workAreaGpsLongtitude"]),
          gpsAreas: normaliseGpsAreaList(metadata["workAreaGpsAreas"]),
        })
      }

      const attachmentItems: UploadedFileItem[] =
        permitDetail.attachments?.map((attachment) => ({
          id: attachment.permitAttachmentId,
          docId: attachment.documentTypeId ?? attachment.documentCode ?? attachment.permitAttachmentId,
          name: attachment.fileName || attachment.documentName || "Unknown document",
          size: attachment.fileSize ?? null,
          type: undefined,
          uploadedAt: attachment.uploadedAt ?? new Date().toISOString(),
          storagePath: attachment.storagePath,
          fileName: attachment.fileName || undefined,
          storedFileName: attachment.storedFileName ?? undefined,
          documentCode: attachment.documentCode ?? undefined,
          uploadedBy: attachment.uploadedBy ?? null,
          status: attachment.status ?? "ACTIVE",
        })) ?? []

      setUploadedFiles(attachmentItems)

      if (permitDetail.riskAssessment) {
        const assessment = permitDetail.riskAssessment
        setRiskData({
          consequence: {
            personnel: assessment.personScore ? String(assessment.personScore) : "",
            equipment: assessment.communityScore ? String(assessment.communityScore) : "",
            environment: assessment.environmentScore ? String(assessment.environmentScore) : "",
            property: assessment.assetScore ? String(assessment.assetScore) : "",
          },
          likelihood: {
            conditions: assessment.quantitativeScore ? String(assessment.quantitativeScore) : "",
            accident: assessment.qualitativeScore ? String(assessment.qualitativeScore) : "",
          },
        })
      } else {
        setRiskData(createEmptyRiskState())
      }
    },
    [permitTypes, permitData.permitType, currentUser.name, currentUser.company, setPermitData, setSelectedActivities, setSelectedWorkers, setExtraFieldValues, setWorkAreaSelection, setUploadedFiles, setRiskData],
  )

  const referenceFlow = usePermitReferenceFlow({
    tenantId: resolvedTenantId,
    requesterId: normalizedCurrentUserId,
    templatePermitCode: templateConfig?.permitCode,
    onDetailResolved: applyReferenceFromPermitDetail,
    onDetailFetched: (detail) => {
      setPermitData((prev) => ({
        ...prev,
        permitType: detail.permitTypeName ?? prev.permitType,
        permitTypeCode: detail.permitTypeCode ?? prev.permitTypeCode,
      }))
    },
  })
  const [isTimelineDialogOpen, setIsTimelineDialogOpen] = useState(false)
  const handlePermitCreated = useCallback(
    (newPermitId: string, response?: CreatePermitResponsePayload) => {
      if (!newPermitId) {
        return
      }
      setEditingPermitId(newPermitId)
      setMode("edit")
      if (response?.permitNumber) {
        setPermitData((prev) => ({ ...prev, permitNo: response.permitNumber }))
      }
    },
    [],
  )

  useEffect(() => {
    if (templateConfig) {
      // Debug: inspect template fields when permit type changes
      const resolveVisibleFlag = (field: any): boolean | undefined => {
        if (typeof field?.visible === "boolean") {
          return field.visible
        }
        const rawConfig = typeof field?.config === "string" ? field.config : null
        if (!rawConfig) {
          return undefined
        }
        try {
          const parsed = JSON.parse(rawConfig) as Record<string, unknown>
          for (const [key, value] of Object.entries(parsed ?? {})) {
            const normalizedKey = String(key).trim().toLowerCase()
            if (normalizedKey === "visible" || normalizedKey === "isvisible") {
              if (typeof value === "boolean") return value
              if (typeof value === "string") {
                const normalized = value.trim().toLowerCase()
                if (normalized === "true") return true
                if (normalized === "false") return false
              }
            }
          }
        } catch {
          // ignore invalid json
        }
        return undefined
      }

      const computedFields =
        templateConfig.sections
          ?.flatMap((section) =>
            (section.fields ?? [])
              .filter((field) => (field.fieldType ?? "").trim().toLowerCase() === "computed")
               .map((field) => ({
                 sectionCode: section.code,
                 fieldId: field.fieldId,
                 code: field.code,
                 label: field.label,
                 fieldType: field.fieldType,
                 layout: field.layout,
                 visible: resolveVisibleFlag(field as any),
                 config: field.config,
               })),
           )
           .filter(Boolean) ?? []

      console.log("[permit][template] loaded", {
        templateId: templateConfig.templateId,
        templateVersionId: templateConfig.templateVersionId,
        computedFields,
        sections: templateConfig.sections?.map((section) => ({
          code: section.code,
          title: section.title,
          fieldCount: section.fields?.length ?? 0,
           fields: section.fields?.map((f) => ({
             fieldId: f.fieldId,
             code: f.code,
             fieldType: f.fieldType,
             label: f.label,
             layout: f.layout,
             visible: resolveVisibleFlag(f as any),
             visibilityRules: f.visibilityRules,
             datasource: f.datasource ? { table: f.datasource.tableName, params: f.datasource.paramsJson } : null,
           })),
         })),
       })
     }
  }, [templateConfig])

  const {
    permitDetail,
    isPermitDetailLoading,
    permitDetailError,
    hasAppliedPermitDetail,
    refreshPermitDetail,
    permitDetailReloadToken,
  } = usePermitDetailHydration({
    tenantId: resolvedTenantId,
    mode,
    permitId: editingPermitId,
    templateConfig,
    permitTypes,
    currentUser,
    setApprovalStateId,
    setPermitData,
    setWorkAreaSelection,
    setSelectedActivities,
    setSelectedWorkers,
    setExtraFieldValues,
    setUploadedFiles,
    setRiskData,
  })

  const shouldLoadWorkflowTimeline =
    mode === "edit" &&
    Boolean(editingPermitId) &&
    hasAppliedPermitDetail &&
    Boolean(approvalStateId)

  useEffect(() => {
    if (mode !== "edit" || !permitDetail) {
      return
    }

    const isGuidLike = (value: string) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)

    const nextLabels: Record<string, string> = {}
    ;(permitDetail.fieldValues ?? []).forEach((field) => {
      const code = (field.fieldCode ?? "").trim().toUpperCase()
      if (!code || !GENERAL_FIELD_STATE_MAP[code]) {
        return
      }
      const displayValue = typeof field.displayValue === "string" ? field.displayValue.trim() : ""
      if (displayValue && !isGuidLike(displayValue)) {
        nextLabels[code] = displayValue
      }
    })

    const metadataLocationName = permitDetail.metadata?.locationName?.trim() ?? ""
    if (
      permitDetail.fieldValues?.some(
        (field) => (field.fieldCode ?? "").trim().toUpperCase() === "WORKLOCATION",
      ) &&
      metadataLocationName &&
      !isGuidLike(metadataLocationName) &&
      !nextLabels.WORKLOCATION
    ) {
      nextLabels.WORKLOCATION = metadataLocationName
    }

    if (Object.keys(nextLabels).length === 0) {
      return
    }

    setGeneralFieldLabels((prev) => {
      let changed = false
      const merged = { ...prev }
      Object.entries(nextLabels).forEach(([key, value]) => {
        if (merged[key] !== value) {
          merged[key] = value
          changed = true
        }
      })
      return changed ? merged : prev
    })
  }, [mode, permitDetail])

  const {
    workflowTimeline,
    isWorkflowTimelineLoading,
    workflowTimelineError,
  } = usePermitWorkflowTimeline({
    tenantId: resolvedTenantId,
    permitId: editingPermitId,
    enabled: shouldLoadWorkflowTimeline,
    reloadToken: permitDetailReloadToken,
  })

  const cancelFlow = usePermitCancelFlow({
    tenantId: resolvedTenantId,
    permitId: editingPermitId,
    permitNumber: permitData.permitNo,
    permitStatus: permitDetail?.status,
    permitMetadata: (permitDetail?.metadata as Record<string, unknown> | null | undefined) ?? null,
    workflowTimeline,
    cancelledByUserId: normalizedCurrentUserId,
    onCancelledSuccess: () => {
      refreshPermitDetail({ hydrateForm: true })
      navigate('/permit-approvals', { state })
    },
  })

  const workflowStageSequences = useMemo<Record<string, number> | null>(() => {
    if (!workflowTimeline?.stages?.length) {
      return null
    }
    const map: Record<string, number> = {}
    workflowTimeline.stages.forEach((stage) => {
      if (typeof stage?.seqIndex !== "number") {
        return
      }
      if (stage.stageId) {
        map[stage.stageId.trim().toLowerCase()] = stage.seqIndex
      }
      if (stage.code) {
        map[stage.code.trim().toUpperCase()] = stage.seqIndex
      }
    })
    return map
  }, [workflowTimeline])

  const workflowUserContext = useMemo(
    () => ({
      ...(state.user ?? {}),
      roles:
        (Array.isArray((state.user as any)?.roles) && (state.user as any).roles) ||
        (Array.isArray((state as any)?.roles) && (state as any).roles) ||
        undefined,
    }),
    [state],
  )

  const activeWorkflowStage = useActiveWorkflowStage({
    timeline: workflowTimeline,
    approvalStateId,
    currentUser: workflowUserContext,
  })

  useEffect(() => {
    if (mode !== "edit") {
      return
    }
    if (!workflowTimeline) {
      console.debug("[PermitRequest] workflow timeline not available", {
        permitId: editingPermitId,
        approvalStateId,
        hasDetail: hasAppliedPermitDetail,
      })
      return
    }

    console.debug("[PermitRequest] workflow timeline snapshot", {
      permitId: editingPermitId,
      approvalStateId,
      stages: workflowTimeline.stages?.map((stage) => ({
        seqIndex: stage.seqIndex,
        stageId: stage.stageId,
        code: stage.code,
        status: stage.status,
        assignedUserId: stage.assignedUserId,
      })),
    })
  }, [workflowTimeline, editingPermitId, approvalStateId, hasAppliedPermitDetail, mode])

  useEffect(() => {
    if (mode !== "edit" || !activeWorkflowStage) {
      return
    }
    console.debug("[PermitRequest] active workflow stage resolved", {
      stageId: activeWorkflowStage.stageId,
      code: activeWorkflowStage.code,
      seqIndex: activeWorkflowStage.seqIndex,
      status: activeWorkflowStage.status,
      assignedUserId: activeWorkflowStage.assignedUserId,
    })
  }, [activeWorkflowStage, mode])

  const workflowMetadata = useWorkflowMetadata({
    mode,
    permitId: editingPermitId,
    activeStage: activeWorkflowStage,
    approvalStateId,
  })

  const {
    requiredDocuments,
    isDocumentsLoading,
    documentsError,
  } = usePermitDocuments({
    tenantId: resolvedTenantId,
    templateConfig,
    selectedActivities,
    uploadedFiles,
  })

  useEffect(() => {
    if (mode === "create") {
      setWorkAreaSelection({
        mapMode: "plot-plan",
        marker: null,
        area: null,
        areas: null,
        gpsLatitude: null,
        gpsLongtitude: null,
        gpsAreas: null,
      })
    }
  }, [mode])

  useEffect(() => {
    // A permit opened via window.open() (e.g. "เปิดดู" in a new tab) has no router
    // state at all, including state.user - don't treat that as "not logged in" when
    // we've got a valid read-only view resolved from the URL instead.
    const isUrlDrivenReadOnlyView = Boolean(searchParams.get("permitId")) && forceReadOnly
    if (!state.user && !isUrlDrivenReadOnlyView) {
      navigate("/modules", { replace: true })
    }
  }, [state.user, navigate, searchParams, forceReadOnly])

  const generalSection = useMemo(() => {
    if (!templateConfig?.sections?.length) {
      return null
    }

    const sections = templateConfig.sections
    const normalize = (value?: string | null) => value?.toUpperCase().trim() ?? ''
    const normalizedGeneralCode = normalize(GENERAL_SECTION_CODE)

    // 1) exact match with "GENERAL" (historical naming)
    const exact = sections.find(
      (section: (typeof sections)[number]) => normalize(section?.code) === normalizedGeneralCode,
    )
    if (exact) {
      return exact
    }

    // 2) fallback for templates that renamed the general section but still start with "GENERAL"
    const prefixed = sections.find((section: (typeof sections)[number]) =>
      normalize(section?.code).startsWith('GENERAL'),
    )
    if (prefixed) {
      return prefixed
    }

    // 3) final fallback: the first requester-owned section (no approval stage)
    return (
      sections.find(
        (section: (typeof sections)[number]) =>
          !section?.approvalStageId && (section?.code ?? '').trim().length > 0,
      ) ?? sections[0] ?? null
    )
  }, [templateConfig])

  const resolvedActiveApprovalStageId = useMemo(() => {
    const activeStageId =
      typeof activeWorkflowStage?.stageId === "string" ? activeWorkflowStage.stageId.trim() : ""
    if (activeStageId) {
      return activeStageId
    }
    const fallback = typeof approvalStateId === "string" ? approvalStateId.trim() : ""
    return fallback || null
  }, [activeWorkflowStage?.stageId, approvalStateId])
  const normalizedApprovalStageId = useMemo(
    () => resolvedActiveApprovalStageId?.trim().toLowerCase() ?? null,
    [resolvedActiveApprovalStageId],
  )
  const currentUserRoleCodeCandidates = useMemo(
    () => collectNormalizedUserRoleCodeCandidates(workflowUserContext),
    [workflowUserContext],
  )
  const activeStageCodeForSections = useMemo(() => {
    const normalizeCode = (value?: string | null): string | null => {
      if (typeof value !== "string") {
        return null
      }
      const trimmed = value.trim()
      return trimmed ? trimmed.toUpperCase() : null
    }

    const sections = templateConfig?.sections ?? []
    const normalizedStageId = normalizedApprovalStageId
    const stageScopedSections = normalizedStageId
      ? sections.filter(
          (section) => section.approvalStageId?.trim().toLowerCase() === normalizedStageId,
        )
      : sections
    const scopedCodes = new Set(
      stageScopedSections
        .map((section) => normalizeCode(section.code))
        .filter((code): code is string => Boolean(code)),
    )
    const allCodes = new Set(
      sections
        .map((section) => normalizeCode(section.code))
        .filter((code): code is string => Boolean(code)),
    )

    const hasCodeMatch = (code: string): boolean => {
      if (scopedCodes.size > 0) {
        return scopedCodes.has(code)
      }
      return allCodes.has(code)
    }

    const candidates = [
      normalizeCode(activeWorkflowStage?.assignedRoleCode),
      normalizeCode(activeWorkflowStage?.code),
      ...currentUserRoleCodeCandidates,
    ]

    for (const candidate of candidates) {
      if (!candidate) {
        continue
      }
      if (hasCodeMatch(candidate)) {
        return candidate
      }
    }

    if (stageScopedSections.length === 1) {
      const onlyCode = normalizeCode(stageScopedSections[0]?.code)
      if (onlyCode) {
        return onlyCode
      }
    }

    return normalizeCode(activeWorkflowStage?.assignedRoleCode) ?? normalizeCode(activeWorkflowStage?.code) ?? null
  }, [
    activeWorkflowStage?.assignedRoleCode,
    activeWorkflowStage?.code,
    currentUserRoleCodeCandidates,
    normalizedApprovalStageId,
    templateConfig?.sections,
  ])
  const normalizedActiveStageCode = useMemo(
    () => activeStageCodeForSections,
    [activeStageCodeForSections],
  )

  const stageOwnedSections = useMemo(() => {
    if (!templateConfig?.sections?.length) {
      return []
    }
    const generalPrefix = GENERAL_SECTION_CODE.toUpperCase()
    return templateConfig.sections.filter((section) => {
      const normalizedCode = section.code?.toUpperCase() ?? ""
      if (normalizedCode.startsWith(generalPrefix)) {
        return false
      }
      const sectionStageId = section.approvalStageId?.trim().toLowerCase() ?? null
      // Parallel-approver safeguard: when active stage code is known, do not include sibling
      // sections that share stageId but target a different stage code (e.g. ENIN vs ENEL).
      if (normalizedActiveStageCode && normalizedCode) {
        if (normalizedCode === normalizedActiveStageCode) {
          return true
        }
        if (normalizedApprovalStageId && sectionStageId === normalizedApprovalStageId) {
          return false
        }
      }
      if (normalizedApprovalStageId && sectionStageId === normalizedApprovalStageId) {
        return true
      }
      if (normalizedActiveStageCode && normalizedCode === normalizedActiveStageCode) {
        return true
      }
      return false
    })
  }, [templateConfig, normalizedApprovalStageId, normalizedActiveStageCode])

  const buildPermitPayload = useCallback((): CreatePermitRequestPayload | null => {
    if (!templateConfig || !generalSection) {
      return null
    }

    return buildPermitRequestPayload({
      tenantId: resolvedTenantId,
      templateConfig,
      generalSection,
      permitData,
      extraFieldValues,
      selectedActivities,
      selectedWorkers,
      workAreaSelection,
      riskData,
      uploadedFiles,
      workflowMetadata,
      currentUser,
      existingRequesterId: permitDetail?.requesterId ?? null,
      siteId: state.site?.id ?? null,
    })
  }, [
    templateConfig,
    generalSection,
    permitData,
    extraFieldValues,
    selectedActivities,
    selectedWorkers,
    workAreaSelection,
    riskData,
    uploadedFiles,
    workflowMetadata,
    permitDetail?.requesterId,
    currentUser,
    state.site?.id,
    resolvedTenantId,
  ])

  const submitterUserId = normalizedCurrentUserId

  const changeWorkTimeFlow = useChangeWorkTimeFlow({
    tenantId: resolvedTenantId,
    editingPermitId,
    submitterUserId,
    permitData: {
      permitNo: permitData.permitNo,
      startDate: permitData.startDate,
      endDate: permitData.endDate,
      startTime: permitData.startTime,
      endTime: permitData.endTime,
    },
    navigationState: state,
  })

  const extendPermitFlow = useExtendPermitFlow({
    tenantId: resolvedTenantId,
    editingPermitId,
    submitterUserId,
    permitData: {
      permitNo: permitData.permitNo,
      startDate: permitData.startDate,
      startTime: permitData.startTime,
      endDate: permitData.endDate,
      endTime: permitData.endTime,
    },
    navigationState: state,
  })

  const {
    handleSave,
    isSaving,
    saveError,
    saveMessage,
    handleSimulateWorkflow,
    isSimulatingWorkflow,
    simulationResult,
    simulationError,
    isSimulationDialogOpen,
    setIsSimulationDialogOpen,
    handleSubmitWorkflow,
    isSubmittingWorkflow,
    submitError,
    submitMessage,
    ensurePermitSaved,
  } = usePermitRequestActions({
    tenantId: resolvedTenantId,
    templateConfig,
    generalSection,
    permitData,
    requiredDocuments,
    uploadedFiles,
    buildPermitPayload,
    editingPermitId,
    refreshPermitDetail,
    submitterUserId,
    onPermitCreated: handlePermitCreated,
  })

  const actionContextValue = useMemo(
    () => ({
      handleSave,
      isSaving,
      saveError,
      saveMessage,
      handleSimulateWorkflow,
      isSimulatingWorkflow,
      simulationResult,
      simulationError,
      isSimulationDialogOpen,
      setIsSimulationDialogOpen,
      handleSubmitWorkflow,
      isSubmittingWorkflow,
      submitError,
      submitMessage,
      ensurePermitSaved,
    }),
    [
      handleSave,
      isSaving,
      saveError,
      saveMessage,
      handleSimulateWorkflow,
      isSimulatingWorkflow,
      simulationResult,
      simulationError,
      isSimulationDialogOpen,
      setIsSimulationDialogOpen,
      handleSubmitWorkflow,
      isSubmittingWorkflow,
      submitError,
      submitMessage,
      ensurePermitSaved,
    ],
  )

  const handleNavigate = (page: string) => {
    if (page === "home") {
      navigate("/dashboard", { state })
    }
  }

  const permitStatus = normalizePermitStatus(permitDetail?.status) ?? DRAFT_STATUS
  const permitStatusCode = permitDetail?.status ?? permitStatus
  const permitStatusName = permitDetail?.statusName ?? undefined
  // Mirrors PermitWorkflowSectionsPanel's canEditSection bypass: ENQA (and any other role in
  // ALWAYS_EDITABLE_SECTION_ROLE_CODES) can edit their audit section before the workflow reaches
  // their own stage, but only within ALWAYS_EDITABLE_STATUS_CODES (go-live scoped this to
  // P0251/P0252/P0253, not every stage/status). That only unlocked the section's own fields
  // though - Save stayed hidden (isApprovalEntryMode) and disabled (canEditGeneralSection), since
  // both are keyed off the *current active stage*, not "does this user own an always-editable
  // section here."
  const hasEnqaAlwaysEditableAccess = useMemo(() => {
    if (!ALWAYS_EDITABLE_STATUS_CODES.includes(permitStatus)) {
      return false
    }
    const sections = templateConfig?.sections ?? []
    return sections.some((section) => {
      const sectionCode = section.code?.trim().toUpperCase() ?? ""
      return (
        ALWAYS_EDITABLE_SECTION_ROLE_CODES.includes(sectionCode) &&
        currentUserRoleCodeCandidates.includes(sectionCode)
      )
    })
  }, [permitStatus, templateConfig?.sections, currentUserRoleCodeCandidates])
  const isCancelledPermit = isCancelledStatus(permitStatusCode)
  const normalizedRequesterId = useMemo(() => {
    if (!permitDetail?.requesterId) {
      return null
    }
    const trimmed = permitDetail.requesterId.trim()
    return trimmed.length > 0 ? trimmed.toLowerCase() : null
  }, [permitDetail?.requesterId])
  const isCurrentUserRequester = useMemo(() => {
    if (!normalizedRequesterId) {
      return true
    }
    if (!normalizedCurrentUserId) {
      return false
    }
    return normalizedRequesterId === normalizedCurrentUserId
  }, [normalizedRequesterId, normalizedCurrentUserId])
  const normalizedPermitCompanyId = useMemo(() => {
    const detailRecord = permitDetail as (PermitDetail & { companyId?: string | null; requesterCompanyId?: string | null }) | null
    const rawCandidates = [
      detailRecord?.companyId,
      detailRecord?.requesterCompanyId,
      permitDetail?.metadata?.["requestorCompanyId"] ?? null,
      permitDetail?.metadata?.["companyId"] ?? null,
    ]

    for (const candidate of rawCandidates) {
      if (typeof candidate !== "string") {
        continue
      }
      const trimmed = candidate.trim().toLowerCase()
      if (trimmed) {
        return trimmed
      }
    }

    return null
  }, [permitDetail])
  const normalizedPermitCompanyName = useMemo(() => {
    const candidates = [
      permitDetail?.requesterCompany,
      permitDetail?.metadata?.["requestorCompany"] ?? null,
      permitData.requestorCompany,
    ]

    for (const candidate of candidates) {
      const normalized = normalizeCompanyToken(candidate)
      if (normalized) {
        return normalized
      }
    }

    return ""
  }, [normalizeCompanyToken, permitData.requestorCompany, permitDetail])
  const normalizedCurrentUserCompanyName = useMemo(
    () => normalizeCompanyToken(currentUser.company),
    [currentUser.company, normalizeCompanyToken],
  )
  const isCurrentUserSameCompany = useMemo(() => {
    if (!isContractorUser) {
      return false
    }

    if (resolvedCompanyId && normalizedPermitCompanyId) {
      return resolvedCompanyId === normalizedPermitCompanyId
    }

    if (normalizedCurrentUserCompanyName && normalizedPermitCompanyName) {
      return normalizedCurrentUserCompanyName === normalizedPermitCompanyName
    }

    return false
  }, [
    isContractorUser,
    normalizedCurrentUserCompanyName,
    normalizedPermitCompanyId,
    normalizedPermitCompanyName,
    resolvedCompanyId,
  ])
  const canManageRequesterSidePermit = useMemo(() => {
    if (isCurrentUserSameCompany) {
      return true
    }
    if (isContractorUser) {
      return Boolean(normalizedRequesterId) && isCurrentUserRequester
    }
    return isCurrentUserRequester
  }, [isContractorUser, isCurrentUserRequester, isCurrentUserSameCompany, normalizedRequesterId])
  const normalizedControllerId = useMemo(() => {
    const raw =
      (permitData.controller as any)?.value ??
      permitDetail?.fieldValues?.find((field) => (field.fieldCode ?? "").toUpperCase() === "CONTROLLER")?.valueText ??
      permitDetail?.metadata?.["controller"] ??
      permitDetail?.metadata?.["controllerId"] ??
      null
    if (typeof raw !== "string") {
      return null
    }
    const trimmed = raw.trim()
    return trimmed ? trimmed.toLowerCase() : null
  }, [permitData.controller, permitDetail?.fieldValues, permitDetail?.metadata])
  const isEditableStatus = isEditablePermitStatus(permitStatus)
  const isRevisionState = isRevisionStatus(permitStatus)
  const isRequesterInformationState = isRequesterInformationStatus(permitStatus)
  const isRequesterRevisionStage =
    (activeWorkflowStage?.code ?? "").trim().toUpperCase() === "REQUESTER_REVISION"
  const isExcavationPermit =
    (permitDetail?.permitTypeCode ?? permitData.permitTypeCode ?? "").trim().toUpperCase() === "EXCAVATION_WORK"
  const requesterInfoActionVisibility = getRequesterInfoActionVisibility({
    isRequesterInformationState,
    isExcavationPermit,
  })
  const hasWorkflowInstance = Boolean(
    permitDetail?.approvalStateId ||
      workflowMetadata?.workflowCurrentStageId ||
      workflowTimeline?.permitWorkflowInstanceId,
  )
  const isPreWorkflowState = mode === "edit" && permitDetail != null && !hasWorkflowInstance
  const requiresRequesterOwnershipForEdit = isRevisionState || isRequesterInformationState
  const canEditForStatus =
    (isEditableStatus && (!requiresRequesterOwnershipForEdit || canManageRequesterSidePermit)) ||
    (isPreWorkflowState && canManageRequesterSidePermit)
  const isPermitLocked = mode === "edit" && permitDetail != null && !canEditForStatus

  const {
    currentStageDisplayName,
    canApproveCurrentStage,
    approvalNote,
    setApprovalNote,
    rejectNote,
    setRejectNote,
    rejectNoteError,
    approvalSuccessMessage,
    approvalActionError,
    isApproveDialogOpen,
    setIsApproveDialogOpen,
    isRejectDialogOpen,
    setIsRejectDialogOpen,
    isApprovingStage,
    isRejectingStage,
    handleOpenRejectDialog,
    handleConfirmApproveStage,
    handleConfirmRejectStage,
    latestRevisionNote,
    latestRequestReviewNote,
  } = useWorkflowApprovalActions({
    tenantId: resolvedTenantId,
    mode,
    permitStatus,
    activeWorkflowStage,
    editingPermitId,
    currentUser: workflowUserContext,
    refreshPermitDetail,
    workflowTimeline,
    canActAsRequesterForInfoRequest: isContractorUser ? canManageRequesterSidePermit : isCurrentUserRequester,
  })

  const {
    missingApprovalStageFields,
    stageValidationError,
    requiredFieldSubmitError,
    submitValidationTargetKeys,
    submitValidationAttemptToken,
    ensureStageRequirementsSatisfied,
    validateBeforeSubmit,
  } = usePermitValidation({
    templateConfig,
    generalSection,
    stageOwnedSections,
    permitData,
    extraFieldValues,
    selectedActivities,
    riskData,
    requiredDocuments,
    uploadedFiles,
    canApproveCurrentStage,
  })

  const [isApprovalFormDialogOpen, setIsApprovalFormDialogOpen] = useState(false)


  const handleConfirmApproveStageWithSave = useCallback(async () => {
    const stageReady = ensureStageRequirementsSatisfied()
    if (!stageReady) {
      return false
    }
    const saveResult = await ensurePermitSaved()
    if (!saveResult.succeeded) {
      return false
    }
    const approved = await handleConfirmApproveStage()
    if (!approved) {
      return false
    }
    if (isRequesterRevisionStage) {
      navigate("/permit-index", { state })
      return true
    }
    navigate("/permit-approvals", { state })
    return true
  }, [ensurePermitSaved, handleConfirmApproveStage, ensureStageRequirementsSatisfied, isRequesterRevisionStage, navigate, state])

  const handleOpenApproveDialogWithValidation = useCallback(() => {
    setIsApprovalFormDialogOpen(true)
  }, [])

  const handleConfirmApproveWithForm = useCallback(async () => {
    const success = await handleConfirmApproveStageWithSave()
    if (success) {
      setIsApprovalFormDialogOpen(false)
    }
  }, [handleConfirmApproveStageWithSave])

  const handleConfirmRejectStageWithSave = useCallback(async () => {
    const saveResult = await ensurePermitSaved()
    if (!saveResult.succeeded) {
      return
    }
    const rejected = await handleConfirmRejectStage()
    if (!rejected) {
      return
    }
    navigate("/permit-approvals", { state })
  }, [ensurePermitSaved, handleConfirmRejectStage, navigate, state])

  const handleApproveDialogOpenChange = useCallback(
    (open: boolean) => {
      if (isApprovingStage) {
        return
      }
      setIsApproveDialogOpen(open)
      if (!open) {
        setApprovalNote("")
      }
    },
    [isApprovingStage, setApprovalNote, setIsApproveDialogOpen],
  )

  const handleRejectDialogOpenChange = useCallback(
    (open: boolean) => {
      if (isRejectingStage) {
        return
      }
      setIsRejectDialogOpen(open)
      if (!open) {
        setRejectNote("")
      }
    },
    [isRejectingStage, setIsRejectDialogOpen, setRejectNote],
  )

  const isFormUnavailable =
    isTemplateLoading ||
    isLoadingPermitTypes ||
    isPermitDetailLoading ||
    (mode === "edit" && !permitDetail && !permitDetailError) ||
    (mode === "edit" && !hasAppliedPermitDetail) ||
    (!!permitDetailError && mode === "edit") ||
    !templateConfig ||
    !generalSection ||
    (mode === "edit" && !permitData.permitNo)

  const isInitialPermitDetailLoading =
    mode === "edit" &&
    (isPermitDetailLoading ||
      (mode === "edit" && !permitDetail && !permitDetailError) ||
      (mode === "edit" && !hasAppliedPermitDetail) ||
      isTemplateLoading ||
      isLoadingPermitTypes)

  const isFormReadOnly = forceReadOnly || isPermitLocked

  const canEditGeneralSection = !isPermitLocked || canApproveCurrentStage || hasEnqaAlwaysEditableAccess
  const isSaveDisabled = isFormUnavailable || !canEditGeneralSection

  // normalizedPermitCompanyId (from permitDetail.companyId/requesterCompanyId/metadata) can hold
  // a stale/unrelated company id that doesn't match the workflow's actual assigned-user company,
  // which short-circuits isCurrentUserSameCompany's id-based check before it ever reaches its own
  // name-based fallback. canApproveCurrentStage already resolves the correct same-company match
  // for the active stage (backend-sourced assignedUserCompanyId vs currentUser.companyId), so for
  // the P0251/REQUESTER_REVISION case, defer to it as a second source instead of re-deriving.
  const canCancelPermit =
    mode === "edit" &&
    !isDraftStatus(permitStatus) &&
    !isCancelledStatus(permitStatus) &&
    ((isContractorUser ? canManageRequesterSidePermit : isCurrentUserRequester) ||
      (isRequesterInformationState && isRequesterRevisionStage && canApproveCurrentStage))
  // A contractor colleague responding to a requester-info request (P0251/REQUESTER_REVISION)
  // typically opens the permit via an "approve" entry point, which otherwise hides Cancel -
  // that suppression is meant for internal reviewers, not the requester side acting here.
  const showCancelDuringApprovalEntry =
    isContractorUser && isRequesterInformationState && isRequesterRevisionStage
  const canCurrentUserChangeWorkTime = useMemo(() => {
    if (isContractorUser && canManageRequesterSidePermit) {
      return true
    }
    if (!normalizedCurrentUserId) {
      return false
    }
    if (normalizedRequesterId && normalizedRequesterId === normalizedCurrentUserId) {
      return true
    }
    if (normalizedControllerId && normalizedControllerId === normalizedCurrentUserId) {
      return true
    }
    return false
  }, [
    canManageRequesterSidePermit,
    isContractorUser,
    normalizedControllerId,
    normalizedCurrentUserId,
    normalizedRequesterId,
  ])
  const canChangeWorkTime =
    mode === "edit" &&
    Boolean(editingPermitId) &&
    isReadyToOpenStatus(permitStatus) &&
    canCurrentUserChangeWorkTime
  const canExtendPermit =
    mode === "edit" &&
    Boolean(editingPermitId) &&
    isOpenStatus(permitStatus) &&
    canCurrentUserChangeWorkTime
  const extraPermitAction = useMemo(() => {
    if (canExtendPermit) {
      return {
        label: THAI_EXTEND_PERMIT_LABEL,
        onClick: extendPermitFlow.actions.openDialog,
        disabled: extendPermitFlow.state.isSubmitting,
        className: "min-w-[180px] border-blue-300 text-blue-700 hover:bg-blue-50 hover:text-blue-800",
      }
    }

    if (canChangeWorkTime) {
      return {
        label: THAI_CHANGE_WORK_TIME_LABEL,
        onClick: changeWorkTimeFlow.actions.openDialog,
        disabled: changeWorkTimeFlow.state.isSubmitting,
        className: "min-w-[220px] border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800",
      }
    }

    return null
  }, [
    canChangeWorkTime,
    canExtendPermit,
    changeWorkTimeFlow.actions.openDialog,
    changeWorkTimeFlow.state.isSubmitting,
    extendPermitFlow.actions.openDialog,
    extendPermitFlow.state.isSubmitting,
  ])

  const isApprovalEntryMode = effectiveEntryMode === "approve"
  const canOpenTimeline = Boolean(editingPermitId)
  const canSubmitWorkflow =
    !isCancelledPermit && !isPermitLocked && !canApproveCurrentStage && !isApprovalEntryMode
  const shouldShowSubmitButton = !isCancelledPermit && !isPermitLocked && !canApproveCurrentStage
  const submitButtonDisabled = !canSubmitWorkflow || isFormUnavailable
  const hasResolvedWorkflowStateForPostActionCheck = useMemo(() => {
    if (mode !== "edit" || !editingPermitId) {
      return false
    }

    return Boolean(
      permitDetail?.approvalStateId ||
        workflowTimeline?.permitWorkflowInstanceId ||
        activeWorkflowStage?.stageId ||
        workflowMetadata?.workflowCurrentStageId,
    )
  }, [
    mode,
    editingPermitId,
    permitDetail?.approvalStateId,
    workflowTimeline?.permitWorkflowInstanceId,
    activeWorkflowStage?.stageId,
    workflowMetadata?.workflowCurrentStageId,
  ])

  useEffect(() => {
    if (!pendingPostActionCheck) {
      return
    }

    const isReloading =
      isPermitDetailLoading || isWorkflowTimelineLoading || isApprovingStage || isSubmittingWorkflow

    if (isReloading) {
      if (!hasObservedPostActionReload) {
        setHasObservedPostActionReload(true)
      }
      return
    }

    if (!hasObservedPostActionReload && !hasResolvedWorkflowStateForPostActionCheck) {
      return
    }

    if (canApproveCurrentStage) {
      setPendingPostActionCheck(null)
      setHasObservedPostActionReload(false)
      return
    }

    const destination =
      pendingPostActionCheck === "submit" && isContractorUser ? "/permit-index" : "/permit-approvals"

    navigate(destination, { state })
    setPendingPostActionCheck(null)
    setHasObservedPostActionReload(false)
  }, [
    pendingPostActionCheck,
    isContractorUser,
    isPermitDetailLoading,
    isWorkflowTimelineLoading,
    isApprovingStage,
    isSubmittingWorkflow,
    hasObservedPostActionReload,
    hasResolvedWorkflowStateForPostActionCheck,
    canApproveCurrentStage,
    navigate,
    state,
  ])

  const formatTimelineDateTime = useCallback((value: string | null | undefined) => {
    if (!value) {
      return null
    }
    const timestamp = Date.parse(value)
    if (Number.isNaN(timestamp)) {
      return null
    }
    return new Intl.DateTimeFormat("th-TH", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(timestamp)
  }, [])

  const formattedLatestRevisionNote = useMemo(() => {
    if (!latestRevisionNote) {
      return null
    }
    return {
      ...latestRevisionNote,
      actedAt: formatTimelineDateTime(latestRevisionNote.actedAt),
    }
  }, [formatTimelineDateTime, latestRevisionNote])

  const formattedLatestRequestReviewNote = useMemo(() => {
    if (!latestRequestReviewNote) {
      return null
    }
    return {
      ...latestRequestReviewNote,
      actedAt: formatTimelineDateTime(latestRequestReviewNote.actedAt),
    }
  }, [formatTimelineDateTime, latestRequestReviewNote])

  const summaryActionNotice: SummaryActionNotice | null = usePermitSummaryActionNotice({
    permitDetail,
    permitStatusCode,
    cancelReasons: cancelFlow.state.cancelReasons,
    workflowTimeline,
    cancelActorName: cancelFlow.state.cancelActorName,
    normalizedRequesterId,
    normalizedControllerId,
    formattedLatestRevisionNote,
    formattedLatestRequestReviewNote,
  })
  const lockedMessage =
    approvalSuccessMessage ??
    (isContractorUser && mode === "edit" && permitDetail != null && !canManageRequesterSidePermit
      ? "รายการนี้ไม่ใช่ Permit ของบริษัทของคุณ จึงไม่สามารถแก้ไขหรือส่งอนุมัติได้"
      : "แบบฟอร์มนี้อนุมัติแล้ว ผู้ขอไม่สามารถปรับข้อมูลต่อได้")
  const handleSubmitWithValidation = useCallback(async () => {
    if (!validateBeforeSubmit()) {
      return
    }
    const submitted = await handleSubmitWorkflow()
    if (!submitted) {
      return
    }
    setPendingPostActionCheck(null)
    setHasObservedPostActionReload(false)
    navigate("/permit-index", { state })
  }, [validateBeforeSubmit, handleSubmitWorkflow, navigate, state])

  const actionMessage = isPermitLocked ? lockedMessage : submitMessage ?? saveMessage
  const actionError =
    stageValidationError ??
    requiredFieldSubmitError ??
    approvalActionError ??
    submitError ??
    saveError ??
    permitDetailError

  const timelineDialog = {
    open: isTimelineDialogOpen,
    onOpenChange: setIsTimelineDialogOpen,
    data: workflowTimeline,
    isLoading: isWorkflowTimelineLoading,
    error: workflowTimelineError,
  }

  const simulationDialog = {
    open: isSimulationDialogOpen,
    setOpen: setIsSimulationDialogOpen,
    isLoading: isSimulatingWorkflow,
    error: simulationError,
    result: simulationResult,
  }

  const approveDialogConfig = {
    open: isApproveDialogOpen,
    isProcessing: isApprovingStage,
    stageName: currentStageDisplayName ?? "",
    note: approvalNote,
    error: approvalActionError,
    onNoteChange: setApprovalNote,
    onOpenChange: handleApproveDialogOpenChange,
    onConfirm: handleConfirmApproveStageWithSave,
  }

  const rejectDialogConfig = {
    open: isRejectDialogOpen,
    isProcessing: isRejectingStage,
    stageName: currentStageDisplayName ?? "",
    note: rejectNote,
    error: approvalActionError,
    noteError: rejectNoteError,
    onNoteChange: setRejectNote,
    onOpenChange: handleRejectDialogOpenChange,
    onConfirm: handleConfirmRejectStageWithSave,
  }

  // Keep the page mounted while detail data is hydrating.
  // The old blocking return is intentionally disabled because it can interrupt
  // downstream form/sidebar mount flow and leave summary/general data incomplete.
  if (false && isInitialPermitDetailLoading) {
    return (
      <div className="space-y-6">
        <PageTitle
          currentPage="permit-form"
          onNavigate={handleNavigate}
          titleOverride={pageCopy.title}
          descriptionOverride={pageCopy.description}
        />

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex min-h-[320px] flex-col items-center justify-center gap-4 py-16 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <div className="space-y-1">
              <p className="text-base font-semibold text-slate-800">กำลังโหลดข้อมูลใบอนุญาต</p>
              <p className="text-sm text-slate-500">กรุณารอสักครู่ ระบบกำลังดึงรายละเอียด Permit มาแสดง</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <PermitRequestActionProvider value={actionContextValue}>
      <div className="space-y-6">
        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-start">
          {isInitialPermitDetailLoading ? (
            <div className="absolute inset-0 z-20 flex items-start justify-center rounded-3xl bg-white/75 px-4 py-16 backdrop-blur-[1px]">
              <div className="flex max-w-md items-center gap-3 rounded-2xl border border-blue-100 bg-white px-5 py-4 text-left shadow-lg">
                <Loader2 className="h-6 w-6 shrink-0 animate-spin text-blue-600" />
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-slate-800">กำลังโหลดข้อมูลใบอนุญาต</p>
                  <p className="text-xs text-slate-500">
                    กรุณารอสักครู่ ระบบกำลังดึงข้อมูลส่วนฟอร์มและ Live Summary
                  </p>
                </div>
              </div>
            </div>
          ) : null}

          <div className="flex-1 space-y-6">
            <PageTitle
              currentPage="permit-form"
              onNavigate={handleNavigate}
              titleOverride={pageCopy.title}
              descriptionOverride={pageCopy.description}
            />

            <PermitRequestForm
              permitData={permitData}
              setPermitData={setPermitData}
              setGeneralFieldLabels={setGeneralFieldLabels}
              generalFieldLabels={generalFieldLabels}
              selectedWorkers={selectedWorkers}
              setSelectedWorkers={setSelectedWorkers}
              selectedActivities={selectedActivities}
              setSelectedActivities={setSelectedActivities}
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              extraFieldValues={extraFieldValues}
              setExtraFieldValues={setExtraFieldValues}
              riskData={riskData}
              setRiskData={setRiskData}
              workAreaSelection={workAreaSelection}
              setWorkAreaSelection={setWorkAreaSelection}
              requiredDocuments={requiredDocuments}
              isDocumentsLoading={isDocumentsLoading}
              documentsError={documentsError}
              currentUser={currentUser}
              permitStatus={permitStatus}
              permitTypes={permitTypes}
              isPermitTypeLoading={isLoadingPermitTypes}
              permitTypeError={permitTypesError}
              templateConfig={templateConfig}
              isTemplateLoading={isTemplateLoading}
              templateError={templateError}
              tenantId={resolvedTenantId}
              isEditMode={mode === "edit"}
              isReadOnly={isFormReadOnly}
              activeApprovalStageId={resolvedActiveApprovalStageId}
              canApproveCurrentStage={canApproveCurrentStage}
              workflowStageSequences={workflowStageSequences}
              currentStageSequence={
                workflowMetadata?.workflowCurrentStageSequence ?? activeWorkflowStage?.seqIndex ?? null
              }
              activeStageCode={activeStageCodeForSections}
              workflowTimeline={workflowTimeline}
              submitValidationTargetKeys={submitValidationTargetKeys}
              submitValidationAttemptToken={submitValidationAttemptToken}
              approvalFormDialog={{
                open: isApprovalFormDialogOpen,
                onCancel: () => setIsApprovalFormDialogOpen(false),
                onConfirm: handleConfirmApproveWithForm,
                isProcessing: isApprovingStage,
                missingFields: missingApprovalStageFields,
                stageName: currentStageDisplayName,
                validationError: stageValidationError,
                actionError: approvalActionError,
              }}
            />
          </div>

          <PermitRequestSidebar
            mode={mode}
            isWorkflowTimelineLoading={isWorkflowTimelineLoading}
            onOpenTimeline={() => setIsTimelineDialogOpen(true)}
            permitData={permitData}
            selectedWorkers={selectedWorkers}
            selectedActivities={selectedActivities}
            requiredDocuments={requiredDocuments}
            uploadedFiles={uploadedFiles}
            riskSnapshot={riskSnapshot}
            workAreaSelection={workAreaSelection}
            templateConfig={templateConfig}
            permitStatusCode={permitStatusCode}
            permitStatusName={permitStatusName}
            generalFieldLabels={generalFieldLabels}
            actionNotice={summaryActionNotice}
          />
        </div>

        <ChangeWorkTimeDialog
          {...changeWorkTimeFlow.dialog}
        />

        <ChangeWorkTimeResultDialog {...changeWorkTimeFlow.resultDialog} />

        <ExtendPermitDialog
          {...extendPermitFlow.dialog}
        />

        <ExtendPermitResultDialog {...extendPermitFlow.resultDialog} />

        <ReferencePermitDialog
          {...referenceFlow.dialog}
        />

      <CancelPermitDialog
          {...cancelFlow.dialog}
        />

      {hideActionBar && !hasEnqaAlwaysEditableAccess ? (
        canOpenTimeline || extraPermitAction ? (
          <div className="sticky bottom-0 left-0 right-0 border-t border-gray-200 bg-white/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-white/75">
            <div className="flex w-full items-center justify-between gap-4 px-4 py-4">
              {canOpenTimeline ? (
                <Button
                  type="button"
                  variant="outline"
                  className="min-w-[150px]"
                  onClick={() => setIsTimelineDialogOpen(true)}
                  disabled={isWorkflowTimelineLoading}
                >
                  {isWorkflowTimelineLoading ? "กำลังโหลด..." : "สถานะการอนุมัติ"}
                </Button>
              ) : null}
              <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
              {extraPermitAction ? (
                <Button
                  type="button"
                  variant="outline"
                  className={extraPermitAction.className}
                  onClick={extraPermitAction.onClick}
                  disabled={extraPermitAction.disabled}
                >
                  {extraPermitAction.label}
                </Button>
              ) : null}
              </div>
            </div>
          </div>
        ) : null
      ) : (
        <BottomActionBar
          disabled={isFormUnavailable}
          saveDisabled={isSaveDisabled}
          showSaveButton={
            (!isApprovalEntryMode || hasEnqaAlwaysEditableAccess) &&
            !isCancelledPermit &&
            (!requesterInfoActionVisibility.hideSaveButton || hasEnqaAlwaysEditableAccess)
          }
          message={actionMessage}
          error={actionError}
          onReference={mode === "create" ? referenceFlow.actions.openDialog : undefined}
          referenceDisabled={!normalizedCurrentUserId}
          onDisapprove={canApproveCurrentStage && !requesterInfoActionVisibility.hideReviewActions ? cancelFlow.actions.openDialog : undefined}
          disapproveDisabled={cancelFlow.state.isCancelling || cancelFlow.state.isLoadingReasons}
          onCancel={
            isApprovalEntryMode && !showCancelDuringApprovalEntry
              ? undefined
              : canCancelPermit
                ? cancelFlow.actions.openDialog
                : undefined
          }
          cancelDisabled={!canCancelPermit || cancelFlow.state.isCancelling || cancelFlow.state.isLoadingReasons}
          onSubmit={shouldShowSubmitButton && !requesterInfoActionVisibility.hideSubmitButton ? handleSubmitWithValidation : undefined}
          showSubmitButton={!isApprovalEntryMode && !isCancelledPermit && !requesterInfoActionVisibility.hideSubmitButton}
          submitDisabled={submitButtonDisabled}
          submitLabel={THAI_SUBMIT_APPROVAL_LABEL}
          onApprove={canApproveCurrentStage ? handleOpenApproveDialogWithValidation : undefined}
          approveDisabled={!canApproveCurrentStage || isApprovingStage}
          approveLabel={isRequesterRevisionStage ? (isExcavationPermit ? "ส่งอนุมัติการกลบดิน" : "ส่งข้อมูลเพิ่มเติม") : undefined}
          onReject={canApproveCurrentStage && !requesterInfoActionVisibility.hideReviewActions ? handleOpenRejectDialog : undefined}
          rejectDisabled={!canApproveCurrentStage || isRejectingStage}
          onTimeline={canOpenTimeline ? () => setIsTimelineDialogOpen(true) : undefined}
          timelineDisabled={isWorkflowTimelineLoading}
          isTimelineLoading={isWorkflowTimelineLoading}
          onExtraAction={extraPermitAction?.onClick}
          extraActionDisabled={extraPermitAction?.disabled}
          extraActionLabel={extraPermitAction?.label}
          extraActionClassName={extraPermitAction?.className}
        />
      )}

      <WorkflowDialogsContainer
        simulation={simulationDialog}
        approvalDialog={approveDialogConfig}
        rejectDialog={rejectDialogConfig}
        timeline={timelineDialog}
      />
      </div>
    </PermitRequestActionProvider>
  )
}

